import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiBell, FiCheckCircle, FiTrendingUp, FiRefreshCw, FiServer, FiGlobe, FiAlertTriangle, FiPlus, FiTrash2, FiX, FiSave } = FiIcons;

const Dashboard = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [systemStatus, setSystemStatus] = useState({ web: 'Online', db: 'Connected', push: 'Ready' });
  const [counts, setCounts] = useState({ leads: 0, subscribers: 0 });
  
  // Watchlist State
  const [watchlist, setWatchlist] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newRisk, setNewRisk] = useState({ company: '', issue: '', severity: 'medium', truckId: '' });

  useEffect(() => {
    loadStats();
    loadWatchlist();
  }, []);

  useEffect(() => {
    // Save watchlist whenever it changes
    if (watchlist.length >= 0) { // Allow saving empty array
        localStorage.setItem('ramirez_watchlist', JSON.stringify(watchlist));
    }
  }, [watchlist]);

  const loadStats = () => {
    const leadsStored = localStorage.getItem('ramirez_leads');
    const subsStored = localStorage.getItem('ramirez_subscribers');
    
    let leadsCount = 4;
    let subsCount = 5;
    
    if (leadsStored) {
        try {
            const parsed = JSON.parse(leadsStored);
            if (Array.isArray(parsed)) leadsCount = parsed.length;
        } catch (e) { console.error("Error parsing leads", e); }
    }
    
    if (subsStored) {
        try {
            const parsed = JSON.parse(subsStored);
            if (Array.isArray(parsed)) subsCount = parsed.length;
        } catch (e) { console.error("Error parsing subscribers", e); }
    }
    
    setCounts({
        leads: leadsCount,
        subscribers: subsCount
    });
  };

  const loadWatchlist = () => {
    const stored = localStorage.getItem('ramirez_watchlist');
    if (stored) {
        try {
            setWatchlist(JSON.parse(stored));
        } catch (e) { console.error("Error parsing watchlist", e); }
    } else {
        // Default data for first run
        const defaults = [
            { id: 1, company: 'Smith Logistics', issue: 'Inspection Due in 3 Days', severity: 'high', truckId: 'Fleet-09' },
            { id: 2, company: 'Rivera Transport', issue: 'Inspection Due in 7 Days', severity: 'medium', truckId: 'Truck-42' },
        ];
        setWatchlist(defaults);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      loadStats();
      setSystemStatus({ web: 'Online', db: 'Connected', push: 'Ready' });
      setIsRefreshing(false);
    }, 800);
  };

  const handleStatusCheck = (type) => {
    setSystemStatus(prev => ({ ...prev, [type]: 'Checking...' }));
    setTimeout(() => {
      setSystemStatus(prev => ({
        ...prev,
        [type]: type === 'web' ? 'Online' : type === 'db' ? 'Connected' : 'Ready'
      }));
    }, 800);
  };

  // Watchlist Actions
  const handleAddRisk = (e) => {
    e.preventDefault();
    if (!newRisk.company || !newRisk.issue) return;

    const risk = {
        id: Date.now(),
        ...newRisk
    };
    
    setWatchlist([risk, ...watchlist]);
    setNewRisk({ company: '', issue: '', severity: 'medium', truckId: '' });
    setIsModalOpen(false);
  };

  const handleDeleteRisk = (id) => {
    if (window.confirm('Remove this item from the watchlist?')) {
        setWatchlist(watchlist.filter(item => item.id !== id));
    }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Dashboard Overview</h1>
          <p className="text-gray-400">Welcome back, Oscar. Here's what's happening today.</p>
        </div>
        <button
          onClick={handleRefresh}
          className={`p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white border border-gray-700 transition-all ${isRefreshing ? 'animate-spin text-blue-500' : ''}`}
          title="Refresh Data"
        >
          <SafeIcon icon={FiRefreshCw} />
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-lg bg-blue-900/20 text-blue-500">
                    <SafeIcon icon={FiUsers} className="text-xl" />
                </div>
                <span className="text-green-400 text-sm font-medium bg-green-900/20 px-2 py-1 rounded">+12%</span>
            </div>
            <h3 className="text-gray-400 text-sm font-medium">Total Leads</h3>
            <p className="text-2xl font-bold text-white mt-1">{counts.leads}</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-lg bg-yellow-900/20 text-yellow-500">
                    <SafeIcon icon={FiBell} className="text-xl" />
                </div>
                <span className="text-green-400 text-sm font-medium bg-green-900/20 px-2 py-1 rounded">+5%</span>
            </div>
            <h3 className="text-gray-400 text-sm font-medium">Active Reminders</h3>
            <p className="text-2xl font-bold text-white mt-1">{counts.subscribers}</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-lg bg-green-900/20 text-green-500">
                    <SafeIcon icon={FiCheckCircle} className="text-xl" />
                </div>
                <span className="text-green-400 text-sm font-medium bg-green-900/20 px-2 py-1 rounded">+8%</span>
            </div>
            <h3 className="text-gray-400 text-sm font-medium">Completed Checks</h3>
            <p className="text-2xl font-bold text-white mt-1">42</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors">
            <div className="flex justify-between items-start mb-4">
                <div className="p-3 rounded-lg bg-purple-900/20 text-purple-500">
                    <SafeIcon icon={FiTrendingUp} className="text-xl" />
                </div>
                <span className="text-green-400 text-sm font-medium bg-green-900/20 px-2 py-1 rounded">+15%</span>
            </div>
            <h3 className="text-gray-400 text-sm font-medium">Site Visits</h3>
            <p className="text-2xl font-bold text-white mt-1">1.2k</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Compliance Watchlist */}
        <div className="lg:col-span-2 bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <SafeIcon icon={FiAlertTriangle} className="text-yellow-500" />
              Compliance Watchlist
            </h2>
            <button 
                onClick={() => setIsModalOpen(true)}
                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
            >
                <SafeIcon icon={FiPlus} /> Add Client
            </button>
          </div>
          <div className="space-y-4 flex-1 overflow-y-auto max-h-[400px] pr-2 custom-scrollbar">
            {watchlist.length > 0 ? (
                watchlist.map((risk) => (
                  <div key={risk.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg border border-gray-700 hover:border-gray-500 transition-colors group">
                    <div className="flex items-center gap-4">
                      <div className={`w-2 h-12 rounded-full ${risk.severity === 'high' ? 'bg-red-500' : risk.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'}`}></div>
                      <div>
                        <h4 className="font-bold text-white">{risk.company}</h4>
                        <p className="text-sm text-gray-400">{risk.truckId ? `${risk.truckId} • ` : ''}{risk.issue}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button className="px-3 py-1.5 text-xs font-medium bg-gray-700 text-white rounded hover:bg-blue-600 transition-colors">
                          Contact
                        </button>
                        <button 
                            onClick={() => handleDeleteRisk(risk.id)}
                            className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-900/20 rounded transition-colors"
                            title="Remove from watchlist"
                        >
                            <SafeIcon icon={FiTrash2} />
                        </button>
                    </div>
                  </div>
                ))
            ) : (
                <div className="text-center py-12 text-gray-500">
                    <SafeIcon icon={FiCheckCircle} className="text-4xl mx-auto mb-2 opacity-30" />
                    <p>No compliance risks detected.</p>
                </div>
            )}
          </div>
        </div>

        {/* System Status */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-lg h-fit">
          <h2 className="text-lg font-bold text-white mb-4">System Status</h2>
          <div className="space-y-4">
            <div onClick={() => handleStatusCheck('web')} className="flex justify-between items-center p-4 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-800 rounded-full">
                  <SafeIcon icon={FiGlobe} className={`text-sm ${systemStatus.web === 'Online' ? 'text-green-500' : 'text-yellow-500'}`} />
                </div>
                <span className="text-gray-200">Website Status</span>
              </div>
              <span className={`font-medium ${systemStatus.web === 'Online' ? 'text-green-400' : 'text-yellow-400'}`}>
                {systemStatus.web}
              </span>
            </div>
            <div onClick={() => handleStatusCheck('db')} className="flex justify-between items-center p-4 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-800 rounded-full">
                  <SafeIcon icon={FiServer} className={`text-sm ${systemStatus.db === 'Connected' ? 'text-green-500' : 'text-yellow-500'}`} />
                </div>
                <span className="text-gray-200">Database Connection</span>
              </div>
              <span className={`font-medium ${systemStatus.db === 'Connected' ? 'text-green-400' : 'text-yellow-400'}`}>
                {systemStatus.db}
              </span>
            </div>
            <div onClick={() => handleStatusCheck('push')} className="flex justify-between items-center p-4 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-800 rounded-full">
                  <SafeIcon icon={FiBell} className={`text-sm ${systemStatus.push === 'Ready' ? 'text-yellow-500' : 'text-blue-500'}`} />
                </div>
                <span className="text-gray-200">Push Notifications</span>
              </div>
              <span className={`font-medium ${systemStatus.push === 'Ready' ? 'text-yellow-400' : 'text-blue-400'}`}>
                {systemStatus.push}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Add Risk Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md shadow-2xl animate-fade-in-up overflow-hidden">
                <div className="flex justify-between items-center p-4 border-b border-gray-700 bg-gray-900/50">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <SafeIcon icon={FiAlertTriangle} className="text-yellow-500" />
                        Add to Watchlist
                    </h3>
                    <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white">
                        <SafeIcon icon={FiX} />
                    </button>
                </div>
                <form onSubmit={handleAddRisk} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Company / Owner Name</label>
                        <input 
                            type="text" 
                            required
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="e.g. JD Trucking"
                            value={newRisk.company}
                            onChange={(e) => setNewRisk({...newRisk, company: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Compliance Issue</label>
                        <input 
                            type="text" 
                            required
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="e.g. Inspection Due Soon"
                            value={newRisk.issue}
                            onChange={(e) => setNewRisk({...newRisk, issue: e.target.value})}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Truck ID (Optional)</label>
                            <input 
                                type="text" 
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                placeholder="e.g. Truck #42"
                                value={newRisk.truckId}
                                onChange={(e) => setNewRisk({...newRisk, truckId: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Severity</label>
                            <select 
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                value={newRisk.severity}
                                onChange={(e) => setNewRisk({...newRisk, severity: e.target.value})}
                            >
                                <option value="low">Low (Info)</option>
                                <option value="medium">Medium (Warning)</option>
                                <option value="high">High (Critical)</option>
                            </select>
                        </div>
                    </div>
                    <div className="pt-4 flex gap-3">
                        <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-2 bg-gray-700 rounded-lg text-gray-300 hover:bg-gray-600 transition-colors">Cancel</button>
                        <button type="submit" className="flex-1 py-2 bg-blue-600 rounded-lg text-white hover:bg-blue-700 transition-colors font-bold flex items-center justify-center gap-2">
                            <SafeIcon icon={FiSave} /> Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Dashboard;